import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import {
  User,
  Mail,
  Star,
  Trash2,
  AlertTriangle,
  Loader2,
  LogOut,
  Settings as SettingsIcon,
  ChevronRight,
} from "lucide-react";
import { getMembershipConfig } from "@/lib/membershipUtils";
import DataRestoreBanner from "@/components/account/DataRestoreBanner";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

async function authFetch(path, { method = "GET" } = {}) {
  const token = localStorage.getItem("ta_access_token");
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pictureUrl] = useState(user?.picture_url);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const cfg = getMembershipConfig(user?.membership_type);
  const initials = user?.full_name
    ? user.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : (user?.email?.[0] || "?").toUpperCase();

  const handleDeleteAccountData = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await authFetch("/api/auth/me/data", { method: "DELETE" });
      toast({
        title: "Account data archived",
        description:
          "Your TA Fitness data has been cleared. You have 7 days to restore it by signing back in.",
      });
      setConfirmOpen(false);
      setTimeout(() => base44.auth.logout("/login"), 700);
    } catch (err) {
      toast({
        title: "Couldn't delete account data",
        description: err.message || "Please try again in a moment.",
        variant: "destructive",
      });
      setIsDeleting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-4">
      <h1 className="text-2xl font-display font-bold">My Account</h1>

      {/* 7-day data restore banner — shared component, no-op when the user
          doesn't have an archive sitting in the window. */}
      <DataRestoreBanner />

      {/* Avatar + name */}
      <Card className="p-4 border-0 shadow-sm flex flex-col items-center text-center gap-2">
        {pictureUrl ? (
          <img src={pictureUrl} alt={user?.full_name} className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-base font-bold text-primary">
            {initials}
          </div>
        )}
        <div>
          <p className="text-lg font-semibold leading-tight">{user?.full_name || "—"}</p>
          <p className="text-xs text-muted-foreground">{user?.email}</p>
        </div>
        <Badge className={cfg.className}>
          <Star className="w-3 h-3 mr-1" />
          {cfg.label} Member
        </Badge>
      </Card>

      {/* Info */}
      <Card className="p-4 border-0 shadow-sm divide-y divide-border">
        <div className="flex items-center gap-3 py-2.5">
          <User className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Full Name</p>
            <p className="text-sm font-medium">{user?.full_name || "Not set"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 py-2.5">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="text-sm font-medium">{user?.email}</p>
          </div>
        </div>
      </Card>

      {/* Settings — quick link to the preferences page (notifications,
          units, time format, language). Lives between the read-only
          identity card and the destructive Sign Out / Delete actions so
          it's discoverable but not the primary CTA. */}
      <button
        type="button"
        onClick={() => navigate("/settings")}
        data-testid="account-settings-link"
        className="w-full rounded-lg border-0 shadow-sm bg-card hover:bg-card/80 transition flex items-center gap-3 p-4 text-left"
      >
        <SettingsIcon className="w-4 h-4 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">Settings</p>
          <p className="text-xs text-muted-foreground">
            Notifications, measurement units, time format
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </button>

      {/* Sign Out — neutral CTA, separated from the destructive Delete
          row so the two can't be confused at a glance. Wrapped in a
          confirmation dialog (Apple-style "Are you sure?") so a stray
          tap on a phone in a pocket can't accidentally sign you out and
          force a fresh login. */}
      <Button
        variant="outline"
        onClick={() => setSignOutOpen(true)}
        data-testid="sign-out-btn"
        className="w-full justify-center gap-2"
      >
        <LogOut className="w-4 h-4" /> Sign Out
      </Button>

      {/* Danger zone — Apple App Store requires account deletion to be
          readily accessible, so we keep this in the visible viewport on
          all iPhones (down to iPhone SE 375×667). */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-rose-300/80 mb-2">
          Danger zone
        </p>
        <Button
          variant="outline"
          onClick={() => setConfirmOpen(true)}
          data-testid="delete-account-btn"
          className="w-full justify-center gap-2 border-rose-500/30 bg-rose-500/[0.06] text-rose-300 hover:bg-rose-500/15 hover:text-rose-200 hover:border-rose-400/50"
        >
          <Trash2 className="w-4 h-4" /> Delete Account
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !isDeleting && setConfirmOpen(o)}>
        <DialogContent
          className="max-w-md bg-[#0B1220] border-white/10 text-white"
          data-testid="delete-account-confirm-dialog"
        >
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-300 flex items-center justify-center mb-2">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <DialogTitle className="font-display text-center">
              Delete account?
            </DialogTitle>
            <DialogDescription className="text-white/70 text-center leading-relaxed">
              Deleting your account will permanently remove your TA Fitness
              profile, workout history, and associated data. This action
              cannot be undone after <span className="text-white font-semibold">7 days</span>
              {" "}— sign back in within that window to restore everything.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isDeleting}
              data-testid="delete-account-cancel-btn"
              className="flex-1 border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDeleteAccountData}
              disabled={isDeleting}
              data-testid="delete-account-confirm-btn"
              className="flex-1 bg-rose-500 hover:bg-rose-400 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={signOutOpen} onOpenChange={setSignOutOpen}>
        <DialogContent
          className="max-w-md bg-[#0B1220] border-white/10 text-white"
          data-testid="sign-out-confirm-dialog"
        >
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 flex items-center justify-center mb-2">
              <LogOut className="w-6 h-6" />
            </div>
            <DialogTitle className="font-display text-center">
              Sign out of TA Fitness?
            </DialogTitle>
            <DialogDescription className="text-white/70 text-center leading-relaxed">
              You&apos;ll need to log in again to access your workouts,
              meals, and training history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSignOutOpen(false)}
              data-testid="sign-out-cancel-btn"
              className="flex-1 border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setSignOutOpen(false);
                logout(true);
              }}
              data-testid="sign-out-confirm-btn"
              className="flex-1 bg-sky-500 hover:bg-sky-400 text-white"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Sign Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
